import { useEffect, useState } from 'react';

import Castrolelevate from '../assets/svg/Castrolelevate';
import '../assets/style/HeaderStyles.scss';

import { Language } from '../Services/GlobalData';
import {
  capitalizeWords,
  getData,
  updateData,
  trimAddress,
} from '../Services/Util';
import { fetchListOfRecommendedProductLevel } from '../Services/AppAction';
import { trackEvent } from './../Services/analytics';
import { useNavigate } from 'react-router-dom';
import { MenuID, useMenuLayout } from '../Layouts/MenuLayout';
import { decryptData } from '../Services/decryption';
import { useFavorite } from '../Context/FavoriteContext';
interface ProductItem {
  Applicability: string;
  Code: string;
  ParentCode: string;
  Descripton: string;
  DLP: number;
  PACK_FACTOR: number;
  PROD_PRICE: number;
  TOTAL_DISC_PRICE: number;
  DISC_VAL: number | null;
  DISC_PER: number;
  Brand_Level: number;
  PREVIOUSLY_BOUGHT: string;
}

interface HeaderProps {
  isShowBack: boolean;
  navigate: (screen: string) => void;
  goBack: () => void;
  title: string;
  orderPlaced?: number;
  favCount?: number;
  userLocation?: string;
}

export default function Header(props: HeaderProps) {
  const [searchText, setSearchText] = useState('');
  const [productList, setProductList] = useState<ProductItem[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [appData, setAppData] = useState<any>(null);
  const navigate = useNavigate();
  const { favCount } = useFavorite();
  const { isMenuAccessible } = useMenuLayout();
  const canAccessFavorites = isMenuAccessible(MenuID.FAVORITES);
  // Replaces useFocusEffect — refetch on mount
  useEffect(() => {
    let isActive = true;
    const fetchUserData = async () => {
      const data = await getData('userData');
      if (isActive) {
        setUserData(data);
      }
    };
    fetchUserData();
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    const fetchAppData = async () => {
      const data = await getData('appData');
      setAppData(data);
    };
    fetchAppData();
  }, []);

  useEffect(() => {
    const fetchSearchResults = async () => {
      if (searchText.length >= 2 && userData) {
        try {
          const response = await fetchListOfRecommendedProductLevel(
            userData?.PARTY_ID,
            '4',
          );
          const allProducts: ProductItem[] = Array.isArray(response?.data)
            ? response.data
            : [];

          const lowerSearch = searchText.toLowerCase();
          const filtered = allProducts.filter(
            (item: ProductItem) =>
              item.Descripton.toLowerCase().includes(lowerSearch) ||
              item.Code?.toLowerCase().includes(lowerSearch),
          );

          setProductList(filtered);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching product list:', error);
          setProductList([]);
          setShowSuggestions(false);
        }
      } else {
        setProductList([]);
        setShowSuggestions(false);
      }
    };

    const timeout = setTimeout(() => {
      fetchSearchResults();
    }, 200);

    return () => clearTimeout(timeout);
  }, [searchText, userData]);

  const buildProductImageUrl = (code?: string) => {
    if (!code || !appData) {
      return null;
    }
    const base = appData?.ImageUrl;
    return base
      ? `${base}/${code}.jpg?${decryptData(appData.ImageToken)}`
      : null;
  };

  const goProductDetail = async (item: ProductItem) => {
    Language.Msg_BOT1 = `${item.Descripton} (${item.Code})`;
    const imageUrl = buildProductImageUrl(item.Code);

    // Replaces Image.prefetch — preload via browser
    if (imageUrl) {
      const img = new window.Image();
      img.src = imageUrl;
    }

    const productData = {
      id: item.Code,
      name: item.Descripton,
      image: imageUrl || '',
      size: `${item.PACK_FACTOR}${Language.lbl_Ltr}.`,
      discount: item.DISC_PER
        ? `${item.DISC_PER.toFixed(2)}% ${Language.lbl_rupees_sign}`
        : '',
      price: `${Language.lbl_rupees_sign}${(
        item.PROD_PRICE - item.TOTAL_DISC_PRICE
      ).toFixed(2)}`,
      originalPrice: `${Language.lbl_rupees_sign}${item.PROD_PRICE.toFixed(2)}`,
    };
    const user = await getData('userData');
    trackEvent(
      'product_viewed',
      { product_id: productData.id, product_name: productData.name },
      user?.PARTY_ID,
      user?.mobileNumber,
    );

    await updateData('selectedProduct', productData);
    navigate('/productview');
  };

  // ─── Dashboard Header ───────────────────────────────────────────

  if (props.title === 'Home') {
    return (
      <div className="header-home-container">
        {/* Top Row */}
        <div className="header-top">
          <div className="header-logo">
            <Castrolelevate />
          </div>

          <div className="header-actions">
            {canAccessFavorites && (
              <div className="icon-btn notification-btn">
                <i
                  className="pi pi-heart"
                  onClick={async () => {
                    navigate('/favorites');
                  }}
                  style={{ fontSize: '24px' }}
                />
                {favCount > 0 && (
                  <span className="notification-badge">
                    {favCount > 99 ? '99+' : favCount}
                  </span>
                )}
              </div>
            )}

            <button
              className="profile-btn"
              onClick={() => navigate('/profile')}
            >
              <img
                src={require('../assets/images/profile.png')}
                alt="profile"
                className="profile-image"
              />
            </button>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="header-bottom">
          <button
            className="location-pill"
            onClick={() => navigate('/addressbook')}
          >
            <i className="pi pi-map-marker" />
            <span>
              {capitalizeWords(
                trimAddress(props?.userLocation || 'Unknown Location'),
              )}
            </span>
          </button>

          <button className="orders-pill" onClick={() => navigate('/orders')}>
            <i className="pi pi-box" />
            <span>
              Orders: <strong>{props.orderPlaced ?? 0}</strong>
            </span>
          </button>

          <button className="search-circle" onClick={() => navigate('/search')}>
            <i className="pi pi-search" />
          </button>
        </div>
      </div>
    );}

  // ─── Products Header ────────────────────────────────────────────
  else if (props.title === 'Products') {
    return (
      <div className="header-home-container-prods">
        <div className="header-search-container">
          <div className="header-search-box">
            <input
              className="header-search-input"
              placeholder="Search Product"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setShowSuggestions(!!e.target.value);
              }}
            />

            {searchText.length > 0 && (
              <button
                className="header-search-clear"
                onClick={async () => {
                  const user = await getData('userData');
                  trackEvent(
                    'product_search',
                    { search_text: searchText },
                    user?.PARTY_ID,
                    user?.mobileNumber,
                  );
                  setSearchText('');
                  setProductList([]);
                  setShowSuggestions(false);
                }}
              />
            )}
          </div>

          {showSuggestions && productList.length > 0 && (
            <div className="header-suggestions-container">
              {productList.map((item, index) => (
                <div
                  key={index}
                  className="header-suggestion-item"
                  onClick={() => goProductDetail(item)}
                >
                  {item.Descripton} ({item.Code})
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Generic Back Header ────────────────────────────────────────

  return (
    <div className="generic-header-container">
      {/* Left Section */}
      <div className="header-left-section">
        <button
          className="generic-header-back-arrow"
          onClick={() => navigate(-1)}
        >
          <div className="generic-header-back-circle">
            <i className="pi pi-arrow-left" />
          </div>
        </button>

        <div className="header-title-block">
          <span className="header-title">{props.title}</span>
        </div>
      </div>

      {/* Right Section */}
      <div className="header-right-section">
        <button className="profile-btn" onClick={() => navigate('/profile')}>
          <img
            src={require('../assets/images/profile.png')}
            alt="profile"
            className="profile-image"
          />
        </button>
      </div>
    </div>
  );
}
